/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

/* eslint-disable spire/fenced-imports */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { createRef, ReactNode, useEffect, useState } from "react";
import { css, ThemeProps, withTheme } from "styled-components";
import SwiperCore, { A11y, Autoplay, EffectFade, Navigation, Pagination, Scrollbar } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Autoplay, EffectFade]);

interface OwnProps {
    id: string;
    extendedStyles?: CarouselStyles;
    title?: string;
    maxNumberOfColumns: number;
    slides: CarouselSlide[];
    showArrows?: boolean;
    showDots?: boolean;
    slidesGap?: number;
    autoPlay?: number;
    animationType?: string;
    breakpoints?: any;
    loop?: boolean;
    allowTouchMove?: boolean;
}

type Props = OwnProps & ThemeProps<BaseTheme> & HasShellContext;

export interface CarouselSlide {
    id: string;
    renderComponent: (options?: CarouselSlideOptions) => ReactNode;
}

export interface CarouselSlideOptions {}

export interface CarouselStyles {
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    carouselWrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const carouselStyles: CarouselStyles = {
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;

            .swiper-container {
                visibility: hidden;
            }

            .swiper-navigation {
                display: none;
                justify-content: center;
                margin-top: 40px;
                visibility: hidden;
              
                &.show {
                  display: flex; 
                }

                > div {
                    position: static;
                    left: auto;
                    right: auto;
                    top: auto;
                    width: auto;
                    height: auto;
                    margin-top: 0;
                    z-index: ${getProp("theme.zIndex.default")};

                    &:after {
                        color: ${getColor("common.ui9")};
                        font-size: 20px;
                        font-weight: 700;
                    }

                    &:first-child {
                        margin-right: 20px;
                    }

                    &:last-child {
                        margin-left: 20px;
                    }

                    &:nth-child(2) {
                        > span {
                            width: 10px;
                            height: 10px;
                            border: 1px solid ${getColor("common.ui9")};
                            background: ${getColor("common.background")};
                            transition: all 0.2s linear;
                            margin-right: 10px;

                            &:last-child {
                                margin-right: 0;
                            }

                            &:hover {
                                background: ${getColor("common.ui9")};
                            }

                            &[class*="active"] {
                                border: 0;
                                background: ${getColor("common.ui4")};
                            }
                        }
                    }
                }
            }

            .swiper-slide {
                display: flex;
                height: unset;
            }

            &.initialized {
                .swiper-navigation,
                .swiper-container {
                    visibility: visible;
                }
            }
        `,
    },
    centeringWrapper: {
        css: css`
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            display: flex;
            align-items: center;

            &.initialized {
                display: none;
            }
        `,
    },
    spinner: {
        color: "primary.main",
        css: css`
            margin: auto;
        `,
    },
};

const Carousel = ({
    id,
    extendedStyles,
    title,
    maxNumberOfColumns,
    slides,
    showArrows,
    showDots,
    slidesGap,
    autoPlay,
    animationType,
    breakpoints,
    loop,
    allowTouchMove,
}: Props) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    const [styles] = useState(() => mergeToNew(carouselStyles, extendedStyles));
    const [swiperInstance, setSwiperInstance] = useState(null);
    const [showNavOnResponsive, setShowNavOnResponsive] = useState(false);

    useEffect(() => {
        // eslint-disable-next-line global-require
        require("swiper/swiper-bundle.min.css");
        // eslint-disable-next-line global-require
        require("swiper/css/effect-fade");
    }, []);

    useEffect(() => {
        if (swiperInstance) {
            window.dispatchEvent(new Event("resize"));
        }
    }, [swiperInstance]);

    if (!slides || slides.length === 0) {
        return null;
    }

    const isItLoop = typeof loop === "undefined" ? true : loop;
    const preparedBreakPoints = breakpoints || {
        0: {
            slidesPerView: 1,
        },
        481: {
            slidesPerView: 2,
        },
        769: {
            slidesPerView: 3,
        },
        1025: {
            slidesPerView: maxNumberOfColumns || 4,
        },
    };

    const checkShowNavOnResponsive = (slides: any, breakpoints: any, currentBreakpoint: any)  => {
        for (const breakPoint in breakpoints) {
            if (parseInt(currentBreakpoint, 10) > parseInt(breakPoint, 10)) {
                continue;
            }

            if (slides.length > breakpoints[breakPoint].slidesPerView) {
                setShowNavOnResponsive(true);
                return;
            }
        }

        setShowNavOnResponsive(false);
    };

    return (
        <>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            {title && <Typography {...styles.titleText}>{title}</Typography>}
            <StyledWrapper {...styles.carouselWrapper} className={`${!!swiperInstance && "initialized"}`}>
                <StyledWrapper {...styles.centeringWrapper} className={`${!!swiperInstance && "initialized"}`}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
                <Swiper
                    slidesPerView={maxNumberOfColumns}
                    pagination={{ el: `.swiper-pagination_${id}`, type: "bullets", clickable: true }}
                    navigation={{
                        nextEl: `.swiper-button-next_${id}`,
                        prevEl: `.swiper-button-prev_${id}`,
                    }}
                    loop={isItLoop && slides.length >= maxNumberOfColumns*2}
                    spaceBetween={slidesGap || 0}
                    autoplay={autoPlay ? { delay: autoPlay * 1000 } : false}
                    onResize={swiper => {
                        swiper.update();
                        checkShowNavOnResponsive(slides, preparedBreakPoints, swiper.currentBreakpoint);
                    }}
                    onSwiper={swiper => {
                        checkShowNavOnResponsive(slides, preparedBreakPoints, swiper.currentBreakpoint);
                        setSwiperInstance(swiper as any);
                    }}
                    onSlidesLengthChange={swiper => checkShowNavOnResponsive(slides, preparedBreakPoints, swiper.currentBreakpoint)}
                    {...({ effect: animationType || "slide" } as any)}
                    breakpoints={preparedBreakPoints}
                    allowTouchMove={typeof allowTouchMove === "undefined" ? true : allowTouchMove}
                >
                    {slides.map((slide: any) => {
                        return <SwiperSlide key={slide.id} data-test-key={slide.id} >{slide.renderComponent()}</SwiperSlide>;
                    })}
                </Swiper>
                <StyledWrapper className={`swiper-navigation ${(slides.length > maxNumberOfColumns || showNavOnResponsive) && (showArrows || showDots) && 'show'}`}>
                    {showArrows && (
                        <StyledWrapper
                            as="div"
                            className={`swiper-button-prev swiper-button-prev_${id}`}
                            data-test-selector="prevBtn"
                        />
                    )}
                    {showDots && <StyledWrapper className={`swiper-pagination_${id}`} />}
                    {showArrows && (
                        <StyledWrapper
                            as="div"
                            className={`swiper-button-next swiper-button-next_${id}`}
                            data-test-selector="nextBtn"
                        />
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

Carousel.defaultProps = {
    maxNumberOfColumns: 4,
};

export default withIsInShell(withTheme(Carousel));
